import React, { useState, useMemo } from 'react';

const GENERAL_CHECKS = [
  { id: 'material', label: 'Considered materials (strength / weight / surface / availability)' },
  { id: 'tools', label: 'Confirmed tools/machines are accessible (booking / training / permissions)' },
  { id: 'size', label: 'Checked size vs. machine working area (split or scale if needed)' },
  { id: 'time', label: 'Estimated total time (prep / fabrication / post-processing)' },
  { id: 'precision', label: 'Matched precision/tolerance requirements (fit/clearance tests)' },
  { id: 'cost', label: 'Estimated material & processing cost (including scrap/tests)' },
  { id: 'skills', label: 'Skills are sufficient (or plan for quick learning/support)' },
];

const WORKFLOW_CHECK_TEMPLATES = {
  '3d print': [
    { id: '3dp-overhang', label: 'Overhangs/support strategy is reasonable (eg. angles / removal)' },
    { id: '3dp-time', label: 'Print duration acceptable (eg. split parts / reduce infill if needed)' },
    { id: '3dp-size', label: 'Part fits print bed or split strategy planned' },
    { id: '3dp-material', label: 'Material choice matches use requirements (strength/temperature)' },
    { id: '3dp-post', label: 'Post-processing plan ready (support removal/sanding/drilling)' },
  ],
  'laser': [
    { id: 'las-thickness', label: 'Material thickness matches joints/stacking strategy' },
    { id: 'las-kerf', label: 'Kerf/clearance compensated (eg. use fit test strip)' },
    { id: 'las-bed', label: 'Nesting fits bed area (eg. panels/sublayouts/locating holes)' },
    { id: 'las-post', label: 'Edge/surface handling planned (eg. melt/char/protection film)' },
  ],
  'wire': [
    { id: 'wire-radius', label: 'Min bend radius & springback compensation considered' },
    { id: 'wire-join', label: 'Joining method (eg. crimp/solder/tie) feasible & reachable' },
    { id: 'wire-stability', label: 'Global stability (eg. hang/clip/support) and load paths clear' },
  ],
  'mold': [
    { id: 'mold-draft', label: 'Draft angle / parting line handled (eg. no undercuts)' },
    { id: 'mold-vent', label: 'Gates/vents/shrinkage & demolding plan defined' },
    { id: 'mold-material', label: 'Mold/cast materials compatible (eg. temperature/chemistry)' },
  ],
  'foam': [
    { id: 'foam-toolpath', label: 'Toolpaths/guides feasible (eg. rough-then-detail / keep supports)' },
    { id: 'foam-finish', label: 'Surface finishing planned (eg. coating / sanding / reinforcement)' },
  ],
  'paper': [
    { id: 'paper-unfold', label: 'Unfold seams/overlaps/fiber direction & crease radius OK' },
    { id: 'paper-join', label: 'Joining & glue tabs sufficient and well placed' },
  ],
  'electroplate': [
    { id: 'elec-continuity', label: 'Electrical continuity across all joints verified' },
    { id: 'elec-solution', label: 'Electroplating solution & safety equipment ready' },
    { id: 'elec-anode', label: 'Copper anode prepared and power supply configured' },
  ],
  'epoxy': [
    { id: 'epoxy-layers', label: 'Layer alignment and glue-up sequence planned' },
    { id: 'epoxy-sanding', label: 'Progressive sanding strategy (eg. 120 → 220 grit)' },
    { id: 'epoxy-resin', label: 'Resin application & curing environment ready' },
  ],
  'felt': [
    { id: 'felt-foam', label: 'Foam core sized with adequate margin (eg. 10-20mm)' },
    { id: 'felt-guides', label: 'Guide panels cut and alignment method planned' },
    { id: 'felt-needles', label: 'Needle felting tools & wool colors selected' },
  ],
  'clay': [
    { id: 'clay-base', label: 'Wood base structure assembled and stable' },
    { id: 'clay-fabric', label: 'Fabric wrap and clay mixture preparation planned' },
    { id: 'clay-detail', label: 'Detailing/sculpting tools and drying plan ready' },
  ]
};

function detectWorkflowKey(name) {
  const s = (name || '').toLowerCase();
  if (s.includes('3d') || s.includes('gcode') || s.includes('print')) return '3d print';
  if (s.includes('laser') || s.includes('stack') || s.includes('interlock')) return 'laser';
  if (s.includes('wire')) return 'wire';
  if (s.includes('mold') || s.includes('silicone') || s.includes('casting')) return 'mold';
  if (s.includes('foam')) return 'foam';
  if (s.includes('paper') || s.includes('unfold')) return 'paper';
  if (s.includes('electroplat')) return 'electroplate';
  if (s.includes('epoxy')) return 'epoxy';
  if (s.includes('felt')) return 'felt';
  if (s.includes('clay')) return 'clay';
  return null;
}

const MiniCheck = ({ checked, onChange, label }) => (
  <label style={{
    display: 'flex',
    gap: 8,
    alignItems: 'flex-start',
    fontSize: 13,
    color: '#374151',
    marginBottom: 8,
    cursor: 'pointer',
    lineHeight: '1.4'
  }}>
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      style={{
        marginTop: 2,
        transform: 'scale(1.05)',
        cursor: 'pointer'
      }}
    />
    <span>{label}</span>
  </label>
);

const SectionTitle = ({ children }) => (
  <h3 style={{
    fontSize: 16,
    fontWeight: 600,
    color: '#374151',
    margin: '0 0 12px 0'
  }}>
    {children}
  </h3>
);

const ReflectionPanel = ({ workflowNames = [], onClose }) => {
  const [general, setGeneral] = useState(() => new Set());
  const toggleGeneral = id => setGeneral(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const keys = useMemo(
    () => workflowNames.map(w => ({ name: w, key: detectWorkflowKey(w) })),
    [workflowNames]
  );

  const initialPerWf = useMemo(() => {
    const o = {};
    keys.forEach(({ key }) => { if (key) o[key] = new Set(); });
    return o;
  }, [keys]);

  const [perWorkflow, setPerWorkflow] = useState(initialPerWf);
  const togglePerWf = (key, id) => setPerWorkflow(prev => {
    const next = { ...prev };
    const set = new Set(next[key] || []);
    set.has(id) ? set.delete(id) : set.add(id);
    next[key] = set;
    return next;
  });

  const totalGeneral = GENERAL_CHECKS.length;
  const totalPerWf = Object.entries(perWorkflow).reduce((sum, [k]) => sum + (WORKFLOW_CHECK_TEMPLATES[k]?.length || 0), 0);
  const donePerWf = Object.entries(perWorkflow).reduce((sum, [_, set]) => sum + (set?.size || 0), 0);

  const suggestion = useMemo(() => {
    const missing = [];
    // Check actual items that exist in GENERAL_CHECKS
    if (!general.has('material')) missing.push('Test materials for strength/compatibility');
    if (!general.has('tools')) missing.push('Confirm tool access and training');
    if (!general.has('size')) missing.push('Verify part fits in machine working area');
    if (!general.has('precision')) missing.push('Run tolerance/fit tests with samples');
    if (!general.has('time')) missing.push('Estimate realistic fabrication timeline');
    
    if (missing.length === 0) {
      return 'Great preparation! Consider making a test piece before full production.';
    }
    
    // Show top 2-3 priorities
    const priorities = missing.slice(0, 3);
    return `Key priorities: ${priorities.join(', ')}.`;
  }, [general]);

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.5)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 12,
        width: '100%',
        maxWidth: 900,
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,.3)'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 28px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h1 style={{
              margin: 0,
              fontSize: 22,
              fontWeight: 600,
              color: '#1f2937'
            }}>
              My Reflection
            </h1>
            <p style={{
              margin: '6px 0 0',
              fontSize: 13,
              color: '#6b7280'
            }}>
              {workflowNames.length ?
                `Reflecting on: ${workflowNames.join(', ')}` :
                'No workflows selected'
              }
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 24,
              color: '#6b7280',
              cursor: 'pointer',
              padding: '8px'
            }}
          >
            ×
          </button>
        </div>

        <div style={{ display: 'flex', gap: 24, padding: 24 }}>
          {/* Main content */}
          <div style={{ flex: 2, minWidth: 0 }}>
            {/* General checklist */}
            <section style={{ marginBottom: 24 }}>
              <SectionTitle>General checks</SectionTitle>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 10,
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                padding: 16
              }}>
                {GENERAL_CHECKS.map(item => (
                  <MiniCheck
                    key={item.id}
                    checked={general.has(item.id)}
                    onChange={() => toggleGeneral(item.id)}
                    label={item.label}
                  />
                ))}
              </div>
            </section>

            {/* Workflow-specific */}
            <section>
              <SectionTitle>Workflow-specific points</SectionTitle>
              {keys.map(({ name, key }, idx) => {
                const tpl = key ? WORKFLOW_CHECK_TEMPLATES[key] : [];
                if (!key || !tpl.length) {
                  return (
                    <div key={`${name}-${idx}`} style={{
                      marginBottom: 16,
                      padding: 12,
                      border: '1px dashed #e5e7eb',
                      borderRadius: 10
                    }}>
                      <div style={{
                        fontSize: 14,
                        color: '#374151',
                        fontWeight: 600,
                        marginBottom: 6
                      }}>
                        {name}
                      </div>
                      <div style={{
                        fontSize: 12,
                        color: '#9ca3af'
                      }}>
                        No specific checklist (use general checks).
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={`${name}-${idx}`} style={{
                    marginBottom: 16,
                    padding: 12,
                    border: '1px solid #e5e7eb',
                    borderRadius: 10
                  }}>
                    <div style={{
                      fontSize: 14,
                      color: '#374151',
                      fontWeight: 600,
                      marginBottom: 8
                    }}>
                      {name}
                    </div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr',
                      gap: 8
                    }}>
                      {tpl.map(it => (
                        <MiniCheck
                          key={it.id}
                          checked={perWorkflow[key]?.has(it.id)}
                          onChange={() => togglePerWf(key, it.id)}
                          label={it.label}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </section>
          </div>

          {/* Sidebar */}
          <div style={{ flex: 1, minWidth: 300 }}>
            <div style={{
              position: 'sticky',
              top: 20,
              background: '#fffbeb',
              border: '1px solid #fbbf24',
              borderRadius: 12,
              overflow: 'hidden'
            }}>
              <div style={{
                padding: 14,
                background: '#fef3c7',
                borderBottom: '1px solid #fbbf24'
              }}>
                <div style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: '#92400e'
                }}>
                  Summary
                </div>
              </div>
              <div style={{ padding: 14 }}>
                <div style={{
                  fontSize: 12,
                  color: '#92400e',
                  marginBottom: 16
                }}>
                  Progress: General {general.size}/{totalGeneral} • Specific {donePerWf}/{totalPerWf}
                </div>

                <div style={{
                  background: '#ecfdf5',
                  border: '1px solid #10b981',
                  borderRadius: 8,
                  padding: 12,
                  marginBottom: 12
                }}>
                  <div style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#065f46',
                    marginBottom: 6
                  }}>
                    Next steps
                  </div>
                  <div style={{
                    fontSize: 12,
                    color: '#047857',
                    lineHeight: 1.4
                  }}>
                    {suggestion}
                  </div>
                </div>

                <div style={{
                  fontSize: 11,
                  color: '#92400e',
                  textAlign: 'center',
                  fontStyle: 'italic'
                }}>
                  Quick check before fabricating
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReflectionPanel;